import React from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizedDateInput } from '../../../shared/components';
import { formatMoney, parseNumber, toIsoDate } from '../invoiceForm';
import { CreateInvoicePaymentRequest, InvoicePaymentMethod } from '../types/Invoice';
import catalog from '../../catalog/Catalog.module.css';
import styles from '../Invoice.module.css';

type ManualPaymentMethod = Exclude<InvoicePaymentMethod, 'creditNote' | 'online'>;

const localDateKey = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

type InvoiceAddPaymentModalProps = {
  remaining: number;
  currency: string;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (request: CreateInvoicePaymentRequest) => Promise<string | null>;
};

export const InvoiceAddPaymentModal: React.FC<InvoiceAddPaymentModalProps> = ({
  remaining,
  currency,
  busy,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation(['invoices', 'common']);
  const [method, setMethod] = React.useState<ManualPaymentMethod>('cash');
  const [amount, setAmount] = React.useState(remaining.toFixed(2));
  const [paidAt, setPaidAt] = React.useState(localDateKey());
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const parsedAmount = parseNumber(amount);
  const exceedsRemaining = parsedAmount !== undefined && parsedAmount > remaining;

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onClose]);

  const submit = async () => {
    const parsed = parseNumber(amount);
    if (!parsed || parsed <= 0) {
      setError(t('amountRequired', { ns: 'invoices' }));
      return;
    }
    if (exceedsRemaining && !window.confirm(t('paymentExceedsRemaining', {
      ns: 'invoices',
      amount: formatMoney(remaining, currency),
    }))) {
      return;
    }

    setError(null);
    const failure = await onSubmit({
      method,
      amount: parsed,
      paidAt: toIsoDate(paidAt),
      note: note.trim() || undefined,
    });
    if (failure) {
      setError(failure);
    }
  };

  return (
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-payment-title"
      onClick={() => { if (!busy) onClose(); }}
    >
      <div className={styles.modalCard} onClick={event => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 id="add-payment-title" className={styles.modalTitle}>{t('addPayment', { ns: 'invoices' })}</h3>
          <button className={styles.modalCloseBtn} type="button" aria-label={t('close', { ns: 'common' })} onClick={onClose}>
            ×
          </button>
        </div>
        <div className={styles.modalBody}>
          <p className={styles.hint}>{t('remaining', { ns: 'invoices' })}: {formatMoney(remaining, currency)}</p>
          {exceedsRemaining ? (
            <p className={styles.warningHint}>{t('paymentExceedsRemainingHint', { ns: 'invoices' })}</p>
          ) : null}
          {error ? <div className="error">{error}</div> : null}
          <div className={catalog.field}>
            <label className={catalog.label}>{t('paymentMethod', { ns: 'invoices' })}</label>
            <select
              className={catalog.input}
              value={method}
              onChange={event => setMethod(event.target.value as ManualPaymentMethod)}
            >
              <option value="cash">{t('methodCash', { ns: 'invoices' })}</option>
              <option value="card">{t('methodCard', { ns: 'invoices' })}</option>
              <option value="bank">{t('methodBank', { ns: 'invoices' })}</option>
              <option value="other">{t('methodOther', { ns: 'invoices' })}</option>
            </select>
          </div>
          <div className={catalog.field}>
            <label className={catalog.label}>{t('amount', { ns: 'invoices' })}</label>
            <input
              className={catalog.input}
              inputMode="decimal"
              value={amount}
              onChange={event => setAmount(event.target.value)}
            />
          </div>
          <div className={catalog.field}>
            <label className={catalog.label}>{t('paidAt', { ns: 'invoices' })}</label>
            <LocalizedDateInput
              value={paidAt}
              onValueChange={setPaidAt}
              emptyLabel={t('paidAt', { ns: 'invoices' })}
              fillWidth
            />
          </div>
          <div className={catalog.field}>
            <label className={catalog.label}>{t('note', { ns: 'invoices' })}</label>
            <textarea className={catalog.textarea} value={note} onChange={event => setNote(event.target.value)} />
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className="btn btn-secondary" type="button" disabled={busy} onClick={onClose}>
            {t('cancel', { ns: 'common' })}
          </button>
          <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void submit()}>
            {t('addPayment', { ns: 'invoices' })}
          </button>
        </div>
      </div>
    </div>
  );
};
